// @flow
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RateReviewIcon from '@mui/icons-material/RateReview';
import DescriptionIcon from '@mui/icons-material/Description';

import { GlobalEvent, GlobalState } from './GlobalContext';
import { isOfficeFile, openOfficeFileInNewTab } from './FileViewer';
import * as DmsService from '../supports/dms-service';

function formatDateTime(val) {
  if (!val) return '';
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleString();
  } catch {
    return String(val);
  }
}

/**
 * Sanitizes revision text to prevent AI placeholders (e.g. 'None', 'Not Present', 'N/A', '-')
 * from being mistakenly recognized as deleted/added document words.
 */
function sanitizeRevText(txt, changeType, isRev1) {
  if (!txt) return '';
  const trimmed = String(txt).trim();
  const lower = trimmed.toLowerCase();

  // If this clause was newly Added, Rev 1 has no previous content
  if (isRev1 && changeType && changeType.toLowerCase() === 'added') {
    return '';
  }
  // If this clause was Removed, Rev 2 has no updated content
  if (!isRev1 && changeType && changeType.toLowerCase() === 'removed') {
    return '';
  }

  // Blacklist of common AI placeholder phrases
  const placeholders = [
    'none',
    'not present',
    'none / not present',
    'none/not present',
    'none / deleted',
    'none/deleted',
    'deleted',
    'n/a',
    'na',
    '-',
    '--',
    'null',
    'empty',
    'none.'
  ];

  if (placeholders.includes(lower)) {
    return '';
  }
  return trimmed;
}

/**
 * Longest Common Subsequence (LCS) Word-Level Diffing Engine
 * Ignores whitespace/linebreak variations during comparison to avoid false highlights,
 * while preserving the original newlines and paragraph structure for display.
 * Returns an array of tokens with type: 'unchanged' | 'added' | 'removed'
 */
function computeWordDiff(textA, textB) {
  const strA = (textA || '').trim();
  const strB = (textB || '').trim();

  if (!strA && !strB) return [];
  if (!strA) return [{ type: 'added', text: strB }];
  if (!strB) return [{ type: 'removed', text: strA }];

  // Tokenize words while capturing trailing whitespace/newlines for faithful display
  const tokenize = (str) => {
    const regex = /(\S+)(\s*)/g;
    const tokens = [];
    let match;
    while ((match = regex.exec(str)) !== null) {
      tokens.push({
        word: match[1],
        trailing: match[2],
      });
    }
    return tokens;
  };

  const tokensA = tokenize(strA);
  const tokensB = tokenize(strB);

  const m = tokensA.length;
  const n = tokensB.length;

  const dp = Array.from({ length: m + 1 }, () => new Int32Array(n + 1));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (tokensA[i - 1].word === tokensB[j - 1].word) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  let i = m;
  let j = n;
  const rawDiff = [];
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && tokensA[i - 1].word === tokensB[j - 1].word) {
      // Use revised text's word + trailing formatting (newlines/spaces)
      const trailing = tokensB[j - 1].trailing || (i === m && j === n ? '' : ' ');
      rawDiff.push({ type: 'unchanged', text: tokensB[j - 1].word + trailing });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      const trailing = tokensB[j - 1].trailing || (j === n ? '' : ' ');
      rawDiff.push({ type: 'added', text: tokensB[j - 1].word + trailing });
      j--;
    } else {
      const trailing = tokensA[i - 1].trailing || (i === m ? '' : ' ');
      rawDiff.push({ type: 'removed', text: tokensA[i - 1].word + trailing });
      i--;
    }
  }
  rawDiff.reverse();

  // Merge contiguous tokens of same type
  const merged = [];
  for (const chunk of rawDiff) {
    if (merged.length > 0 && merged[merged.length - 1].type === chunk.type) {
      merged[merged.length - 1].text += chunk.text;
    } else {
      merged.push({ ...chunk });
    }
  }
  return merged;
}

export default function DocRevCompareDialog(props) {
  const { open, onClose, sysID, docDetail, selectedRevisions } = props;

  const [gState] = React.useContext(GlobalState);
  const [, gEventDispatch] = React.useContext(GlobalEvent);

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  const [copiedRev1, setCopiedRev1] = useState(false);
  const [copiedRev2, setCopiedRev2] = useState(false);

  // Determine Revision 1 (Baseline/Earlier) and Revision 2 (Target/Newer)
  const [revA, revB] = useMemo(() => {
    if (!selectedRevisions || selectedRevisions.length < 2) return [null, null];
    const sorted = [...selectedRevisions].sort((a, b) => Number(a.RevNo) - Number(b.RevNo));
    return [sorted[0], sorted[1]];
  }, [selectedRevisions]);

  const runComparison = useCallback(async () => {
    if (!revA || !revB || !docDetail) return;

    setLoading(true);
    setErrMsg('');
    try {
      const docID = docDetail.DocID || (revA && revA.DocID);
      const res = await DmsService.aiCompareDocRevisions_Promise(
        gState.sessionID,
        sysID,
        docID,
        revA.RevNo,
        revB.RevNo,
        '',
        null,
        true
      );

      setComparisonData(res);
    } catch (err) {
      setErrMsg(err?.message || 'Failed to compare document revisions using AI.');
    } finally {
      setLoading(false);
    }
  }, [revA, revB, docDetail, gState.sessionID, sysID]);

  useEffect(() => {
    if (open && revA && revB) {
      setComparisonData(null);
      setErrMsg('');
      setActiveTab(0);
      runComparison();
    }
  }, [open, revA?.RevNo, revB?.RevNo]);

  const handleOpenPdf = (revNo) => {
    if (!docDetail) return;
    const docID = docDetail.DocID || (revA && revA.DocID);
    const targetRev = (revA && revA.RevNo === revNo) ? revA : (revB && revB.RevNo === revNo ? revB : null);
    const filename = targetRev?.FileName || docDetail?.FileName || '';

    if (isOfficeFile(filename)) {
      const url = DmsService.getDownloadUrlWithFilename(gState.sessionID, sysID, docID, revNo, null, null, null, 0, filename);
      openOfficeFileInNewTab({
        url: url,
        filename: filename,
        title: `${docDetail?.Subject || filename} (Rev. ${revNo})`,
        canDownload: docDetail?.Permission >= 2,
      });
    } else {
      const url = DmsService.getDownloadUrl(gState.sessionID, sysID, docID, revNo, null, null, null, 0);
      window.open(url, '_blank');
    }
  };

  const parsedJson = comparisonData?.dataj || null;
  const sectionsList = parsedJson?.sections || [];

  // Resolved Extracted Reference Texts for Rev 1 and Rev 2 (supporting Text, Word, Excel, PDF, and OCR)
  const rev1ExtractedText = useMemo(() => {
    if (comparisonData?.rev1_extracted_text !== undefined && comparisonData?.rev1_extracted_text !== null && comparisonData?.rev1_extracted_text.trim() !== '') {
      return comparisonData.rev1_extracted_text.trim();
    }
    if (sectionsList.length > 0) {
      return sectionsList
        .map(s => sanitizeRevText(s.rev1_text, s.change_type, true))
        .filter(Boolean)
        .join('\n\n');
    }
    return '';
  }, [comparisonData, sectionsList]);

  const rev2ExtractedText = useMemo(() => {
    if (comparisonData?.rev2_extracted_text !== undefined && comparisonData?.rev2_extracted_text !== null && comparisonData?.rev2_extracted_text.trim() !== '') {
      return comparisonData.rev2_extracted_text.trim();
    }
    if (sectionsList.length > 0) {
      return sectionsList
        .map(s => sanitizeRevText(s.rev2_text, s.change_type, false))
        .filter(Boolean)
        .join('\n\n');
    }
    return '';
  }, [comparisonData, sectionsList]);

  const handleCopyRev1 = () => {
    if (!rev1ExtractedText) return;
    navigator.clipboard.writeText(rev1ExtractedText);
    setCopiedRev1(true);
    setTimeout(() => setCopiedRev1(false), 2500);
  };

  const handleCopyRev2 = () => {
    if (!rev2ExtractedText) return;
    navigator.clipboard.writeText(rev2ExtractedText);
    setCopiedRev2(true);
    setTimeout(() => setCopiedRev2(false), 2500);
  };

  // Direct Ground-Truth Word Diff across Entire Extracted Texts
  const rawDocumentDiffs = useMemo(() => {
    const t1 = (rev1ExtractedText || '').trim();
    const t2 = (rev2ExtractedText || '').trim();
    if (t1 || t2) {
      return computeWordDiff(t1, t2);
    }
    return null;
  }, [rev1ExtractedText, rev2ExtractedText]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f8fafc',
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          py: 1.5,
          px: 3,
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              color: '#fff',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
            }}
          >
            <RateReviewIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.15rem', color: '#f8fafc', lineHeight: 1.2 }}>
              Document Revision Comparison
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              {docDetail?.DocNo ? `Doc No: ${docDetail.DocNo} • ` : ''}
              {docDetail?.Subject || docDetail?.Filename || 'Document Comparison'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Re-run Comparison">
            <IconButton
              size="small"
              onClick={() => runComparison()}
              disabled={loading}
              sx={{ color: '#cbd5e1' }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose} sx={{ color: '#cbd5e1' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Revision Meta Bar */}
      {revA && revB && (
        <Box
          sx={{
            py: 1,
            px: 3,
            bgcolor: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          {/* Baseline / Rev A */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              p: 0.6,
              px: 1.5,
              borderRadius: 1.5,
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #0284c7',
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Chip
                  label={`Rev ${revA.RevNo}`}
                  size="small"
                  sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700, fontSize: '0.75rem', height: 20 }}
                />
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                  BASELINE (ORIGINAL)
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>
                {revA.FileName || `Revision_${revA.RevNo}`}
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                {revA.CreatedDate ? formatDateTime(revA.CreatedDate) : ''}
                {revA.CreateUser ? ` by ${revA.CreateUser}` : ''}
              </Typography>
            </Box>
            <Tooltip title="View / Open File">
              <IconButton size="small" onClick={() => handleOpenPdf(revA.RevNo)} sx={{ color: '#0284c7' }}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <CompareArrowsIcon sx={{ color: '#64748b', fontSize: 28 }} />

          {/* Revised / Rev B */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              p: 0.6,
              px: 1.5,
              borderRadius: 1.5,
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #2563eb',
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Chip
                  label={`Rev ${revB.RevNo}`}
                  size="small"
                  sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 700, fontSize: '0.75rem', height: 20 }}
                />
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                  REVISED (NEW)
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>
                {revB.FileName || `Revision_${revB.RevNo}`}
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                {revB.CreatedDate ? formatDateTime(revB.CreatedDate) : ''}
                {revB.CreateUser ? ` by ${revB.CreateUser}` : ''}
              </Typography>
            </Box>
            <Tooltip title="View / Open File">
              <IconButton size="small" onClick={() => handleOpenPdf(revB.RevNo)} sx={{ color: '#2563eb' }}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Tabs Header */}
      <Box sx={{ px: 3, bgcolor: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          indicatorColor="primary"
          textColor="primary"
          sx={{ minHeight: 44 }}
        >
          <Tab
            icon={<RateReviewIcon fontSize="small" />}
            iconPosition="start"
            label="Changes"
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.9rem', minHeight: 44, py: 0.5 }}
          />
          <Tab
            icon={<DescriptionIcon fontSize="small" />}
            iconPosition="start"
            label="Extracted Texts"
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.9rem', minHeight: 44, py: 0.5 }}
          />
        </Tabs>
      </Box>

      {/* Main Content Area */}
      <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8fafc', flex: 1, overflowY: 'auto' }}>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60vh',
              gap: 2,
            }}
          >
            <CircularProgress size={50} thickness={4} sx={{ color: '#6366f1' }} />
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#334155' }}>
              Reading and comparing both revisions...
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Performing word-by-word redlining and full-text track changes comparison.
            </Typography>
          </Box>
        )}

        {!loading && errMsg && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => runComparison()}>
                Retry
              </Button>
            }
            sx={{ mb: 2 }}
          >
            {errMsg}
          </Alert>
        )}

        {/* TAB 0: Changes */}
        {!loading && !errMsg && comparisonData && activeTab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1200, mx: 'auto', width: '100%' }}>
            {/* Legend Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#ffffff', p: 0.8, px: 1.5, borderRadius: 1.5, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>
                  LEGEND:
                </Typography>
                <Box component="del" sx={{ bgcolor: '#fee2e2', color: '#991b1b', textDecoration: 'line-through', px: 0.8, py: 0.2, borderRadius: '4px', fontSize: '0.78rem', fontWeight: 600 }}>
                  - Deleted
                </Box>
                <Box component="ins" sx={{ bgcolor: '#dcfce7', color: '#166534', textDecoration: 'none', borderBottom: '2px solid #22c55e', px: 0.8, py: 0.2, borderRadius: '4px', fontSize: '0.78rem', fontWeight: 600 }}>
                  + Inserted
                </Box>
              </Box>
            </Box>

            {/* Continuous Full Document Paper */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 2,
                bgcolor: '#fff',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                width: '100%',
                minHeight: '400px',
              }}
            >
              {rawDocumentDiffs && rawDocumentDiffs.length > 0 ? (
                <Box
                  sx={{
                    lineHeight: 2.2,
                    fontSize: '1rem',
                    color: '#1e293b',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {rawDocumentDiffs.map((chunk, cIdx) => {
                    if (chunk.type === 'removed') {
                      return (
                        <Box
                          key={cIdx}
                          component="del"
                          sx={{
                            bgcolor: '#fee2e2',
                            color: '#991b1b',
                            textDecoration: 'line-through',
                            px: 0.6,
                            py: 0.15,
                            borderRadius: '4px',
                            mx: '1px',
                            fontWeight: 500,
                            border: '1px solid #fecaca',
                            display: 'inline',
                          }}
                        >
                          {chunk.text}
                        </Box>
                      );
                    }
                    if (chunk.type === 'added') {
                      return (
                        <Box
                          key={cIdx}
                          component="ins"
                          sx={{
                            bgcolor: '#dcfce7',
                            color: '#166534',
                            textDecoration: 'none',
                            borderBottom: '2px solid #22c55e',
                            px: 0.6,
                            py: 0.15,
                            borderRadius: '4px',
                            mx: '1px',
                            fontWeight: 600,
                            border: '1px solid #bbf7d0',
                            display: 'inline',
                          }}
                        >
                          {chunk.text}
                        </Box>
                      );
                    }
                    return (
                      <Typography
                        key={cIdx}
                        component="span"
                        sx={{ color: '#1e293b', fontSize: '1rem', lineHeight: 2.2 }}
                      >
                        {chunk.text}
                      </Typography>
                    );
                  })}
                </Box>
              ) : sectionsList.length > 0 ? (
                sectionsList.map((section, sIdx) => {
                  const cleanText1 = sanitizeRevText(section.rev1_text, section.change_type, true);
                  const cleanText2 = sanitizeRevText(section.rev2_text, section.change_type, false);
                  const diffTokens = computeWordDiff(cleanText1, cleanText2);
                  if (diffTokens.length === 0) return null;

                  return (
                    <Box
                      key={sIdx}
                      sx={{
                        mb: 2.8,
                        lineHeight: 2.1,
                        fontSize: '1rem',
                        color: '#1e293b',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      }}
                    >
                      {diffTokens.map((chunk, cIdx) => {
                        if (chunk.type === 'removed') {
                          return (
                            <Box
                              key={cIdx}
                              component="del"
                              sx={{
                                bgcolor: '#fee2e2',
                                color: '#991b1b',
                                textDecoration: 'line-through',
                                px: 0.6,
                                py: 0.15,
                                borderRadius: '4px',
                                mx: '1px',
                                fontWeight: 500,
                                border: '1px solid #fecaca',
                                display: 'inline',
                              }}
                            >
                              {chunk.text}
                            </Box>
                          );
                        }
                        if (chunk.type === 'added') {
                          return (
                            <Box
                              key={cIdx}
                              component="ins"
                              sx={{
                                bgcolor: '#dcfce7',
                                color: '#166534',
                                textDecoration: 'none',
                                borderBottom: '2px solid #22c55e',
                                px: 0.6,
                                py: 0.15,
                                borderRadius: '4px',
                                mx: '1px',
                                fontWeight: 600,
                                border: '1px solid #bbf7d0',
                                display: 'inline',
                              }}
                            >
                              {chunk.text}
                            </Box>
                          );
                        }
                        return (
                          <Typography
                            key={cIdx}
                            component="span"
                            sx={{ color: '#1e293b', fontSize: '1rem', lineHeight: 2.1 }}
                          >
                            {chunk.text}
                          </Typography>
                        );
                      })}
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', py: 6 }}>
                  No content differences found between the two revisions.
                </Typography>
              )}
            </Paper>
          </Box>
        )}

        {/* TAB 1: Extracted Texts */}
        {!loading && !errMsg && comparisonData && activeTab === 1 && (
          <Box sx={{ maxWidth: 1400, mx: 'auto', width: '100%' }}>
            <Grid container spacing={3}>
              {/* Revision 1 Panel */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderTop: '4px solid #0284c7',
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(100vh - 270px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.03)',
                  }}
                >
                  {/* Panel Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pb: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`Rev ${revA?.RevNo || '1'}`}
                        size="small"
                        sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700, fontSize: '0.82rem' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`${rev1ExtractedText.length} chars`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.72rem', height: 22 }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={copiedRev1 ? <CheckIcon color="success" /> : <ContentCopyIcon fontSize="small" />}
                        onClick={handleCopyRev1}
                        disabled={!rev1ExtractedText}
                        sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.2 }}
                      >
                        {copiedRev1 ? 'Copied' : 'Copy'}
                      </Button>
                    </Box>
                  </Box>

                  {/* Text Content */}
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: 'auto',
                      bgcolor: '#f8fafc',
                      p: 2.5,
                      borderRadius: 1.5,
                      border: '1px solid #e2e8f0',
                      fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace',
                      fontSize: '0.92rem',
                      lineHeight: 1.8,
                      color: '#0f172a',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {rev1ExtractedText || (
                      <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                        No text was extracted for Revision {revA?.RevNo || '1'}.
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Revision 2 Panel */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderTop: '4px solid #2563eb',
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(100vh - 270px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.03)',
                  }}
                >
                  {/* Panel Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pb: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`Rev ${revB?.RevNo || '2'}`}
                        size="small"
                        sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 700, fontSize: '0.82rem' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`${rev2ExtractedText.length} chars`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.72rem', height: 22 }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={copiedRev2 ? <CheckIcon color="success" /> : <ContentCopyIcon fontSize="small" />}
                        onClick={handleCopyRev2}
                        disabled={!rev2ExtractedText}
                        sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.2 }}
                      >
                        {copiedRev2 ? 'Copied' : 'Copy'}
                      </Button>
                    </Box>
                  </Box>

                  {/* Text Content */}
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: 'auto',
                      bgcolor: '#f8fafc',
                      p: 2.5,
                      borderRadius: 1.5,
                      border: '1px solid #e2e8f0',
                      fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace',
                      fontSize: '0.92rem',
                      lineHeight: 1.8,
                      color: '#0f172a',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {rev2ExtractedText || (
                      <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                        No text was extracted for Revision {revB?.RevNo || '2'}.
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: '#fff', borderTop: '1px solid #e2e8f0', justifyContent: 'flex-end' }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: 'none', color: '#64748b', borderColor: '#cbd5e1', px: 3 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DocRevCompareDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  sysID: PropTypes.string,
  docDetail: PropTypes.object,
  selectedRevisions: PropTypes.array,
};
